// ─────────────────────────────────────────────────────────────────────
// ConfiguratorFinaliserPage — /configurer/finaliser
// Page dédiée post-configurateur — choix : payer maintenant OU RDV WhatsApp
// Reçoit la config via sessionStorage['sabril_config_prefill']
// ─────────────────────────────────────────────────────────────────────

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  DA,
  SunOrnament,
  SabrilDivider,
  AwningCorner,
  MimosaBranch,
  SabrilStamp,
  SabrilSignature,
} from '../theme/sabril-da';
import {
  Check,
  ChevronLeft,
  MessageCircle,
  CreditCard,
  FileSignature,
  ShieldCheck,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_BASE = 'https://wa.me/33745522450';

const CHANTIER_LABELS = {
  '01': 'ADN & Positionnement',
  '02': 'Identité visuelle & Branding',
  '03': "Design d'espace & Expérience",
  '04': 'Menu Gagnant',
  '05': 'Packaging & Merchandising',
  '06': 'Marges & Performance financière',
  '07': 'Organisation du dirigeant',
  '08': 'Recrutement & Management',
  '09': 'Système de primes & Performance',
  '10': 'Expérience client & Fidélisation',
  '11': 'Réseaux sociaux & Communication',
  '12': 'Formation IA sur-mesure',
  '13': 'Intégration Resto 360',
  '14': 'Process & SOP',
  '15': 'HACCP & Affichages',
};

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(n);

export default function ConfiguratorFinaliserPage() {
  const navigate = useNavigate();
  const [prefill, setPrefill] = useState(null);
  const [mode, setMode] = useState(null); // 'pay' | 'whatsapp'
  const [contact, setContact] = useState({
    firstName: '', email: '', phone: '', restaurant: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Load prefill
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('sabril_config_prefill');
      if (raw) setPrefill(JSON.parse(raw));
      else {
        // No config → back to configurator
        navigate('/configurer', { replace: true });
      }
    } catch (_e) {
      navigate('/configurer', { replace: true });
    }
    window.scrollTo(0, 0);
    document.title = 'Finaliser mon accompagnement · Sabril';
  }, [navigate]);

  if (!prefill) return null;

  const isQuote = prefill.total_ht == null;
  const chantierList = prefill.full_pack
    ? Object.entries(CHANTIER_LABELS)
    : (prefill.chantiers || []).map((n) => [n, CHANTIER_LABELS[n] || '?']);

  const canSubmit = contact.firstName && contact.email && contact.phone && contact.restaurant;

  const buildWhatsappUrl = () => {
    const lines = [
      'Bonjour Sabril,',
      `Je viens du configurateur — format : ${prefill.format_label || prefill.format}.`,
    ];
    if (prefill.country) lines.push(`Destination : ${prefill.country}.`);
    if (chantierList.length) {
      lines.push(`Chantiers : ${chantierList.map(([n, l]) => `${n}·${l}`).join(' | ')}.`);
    }
    if (!isQuote) lines.push(`Total : ${fmt(prefill.total_ht)} € HT.`);
    lines.push('J\u2019aimerais planifier un appel de 30min.');
    return `${WHATSAPP_BASE}?text=${encodeURIComponent(lines.join('\n'))}`;
  };

  const handlePaySubmit = async () => {
    if (!canSubmit) {
      toast.error('Merci de renseigner vos coordonnées.');
      return;
    }
    setSubmitting(true);
    try {
      // Save quote + prepare contract data
      const res = await fetch(`${BACKEND_URL}/api/configurator/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          format: prefill.format,
          format_label: prefill.format_label,
          country: prefill.country,
          chantiers: prefill.chantiers || [],
          full_pack: !!prefill.full_pack,
          total_ht: prefill.total_ht,
          contact,
          mode: 'contract',
        }),
      });
      if (!res.ok) throw new Error('server_error');
      const data = await res.json();

      // Persist merged prefill (with contact) for contract page
      try {
        sessionStorage.setItem('sabril_config_prefill', JSON.stringify({
          ...prefill,
          contact,
          lead_id: data.lead_id,
        }));
      } catch (_e) { /* ignore */ }

      toast.success('Enregistré · direction contrat.');
      navigate(data.contract_url || '/contrat/configurateur');
    } catch (_e) {
      toast.error('Erreur — contactez-moi sur WhatsApp.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full relative overflow-x-hidden"
      style={{ background: DA.ivory, fontFamily: DA.sans }}
      data-testid="finaliser-page"
    >
      {/* Global decorative background */}
      <div className="pointer-events-none fixed inset-0" style={{ zIndex: 0 }}>
        <AwningCorner position="top-left" size={200} opacity={0.85} />
        <AwningCorner position="bottom-right" size={200} opacity={0.85} />
        <MimosaBranch size={180} position="bottom-left" opacity={0.55} />
      </div>

      <div className="relative max-w-2xl mx-auto px-5 py-8 sm:py-14" style={{ zIndex: 1 }}>
        {/* Back link */}
        <button
          onClick={() => navigate('/configurer')}
          className="inline-flex items-center gap-1.5 mb-6"
          style={{
            fontFamily: DA.mono, fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase',
            color: DA.inkMuted, fontWeight: 700,
          }}
          data-testid="finaliser-back"
        >
          <ChevronLeft size={12} strokeWidth={2.4} />
          Retour au configurateur
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-3">
            <SunOrnament size={28} color={DA.sunBright} />
          </div>
          <p style={{
            fontFamily: DA.mono, fontSize: 10, fontWeight: 800, letterSpacing: '0.32em',
            textTransform: 'uppercase', color: DA.goldMain, marginBottom: 12,
          }}>
            Finaliser mon accompagnement
          </p>
          <h1 style={{
            fontFamily: DA.serif, fontSize: 'clamp(28px, 5.5vw, 44px)', fontWeight: 900,
            letterSpacing: '-0.028em', color: DA.greenDeep, lineHeight: 1.05,
          }}>
            Deux façons<br />d&apos;aller plus loin.
          </h1>
        </div>

        {/* ─── RECAP CARD (concise) ─── */}
        <article
          className="relative overflow-hidden"
          style={{
            background: DA.paper,
            borderRadius: 20,
            padding: '18px 20px',
            border: `0.5px solid ${DA.goldSoft}`,
            boxShadow: '0 14px 32px -14px rgba(11,43,36,0.14)',
          }}
          data-testid="finaliser-recap"
        >
          <AwningCorner position="top-left" size={80} />
          <div className="relative flex items-start justify-between gap-4" style={{ zIndex: 2 }}>
            <div className="flex-1 min-w-0">
              <p style={{
                fontFamily: DA.mono, fontSize: 9, letterSpacing: '0.22em',
                textTransform: 'uppercase', color: DA.goldDeep, fontWeight: 800,
              }}>
                Votre configuration
              </p>
              <p style={{
                fontFamily: DA.serif, fontSize: 18, fontWeight: 900,
                color: DA.greenDeep, letterSpacing: '-0.02em', lineHeight: 1.1, marginTop: 4,
              }}>
                {prefill.format_label || prefill.format}
              </p>
              <p style={{
                fontFamily: DA.sans, fontSize: 12, color: DA.inkMuted, marginTop: 3,
              }}>
                {prefill.full_pack ? 'Pack complet 360° · 15 chantiers' : `${chantierList.length} chantier${chantierList.length > 1 ? 's' : ''} activé${chantierList.length > 1 ? 's' : ''}`}
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p style={{
                fontFamily: DA.mono, fontSize: 9, letterSpacing: '0.22em',
                textTransform: 'uppercase', color: DA.goldDeep, fontWeight: 800,
              }}>
                Total
              </p>
              <p style={{
                fontFamily: DA.serif, fontSize: 24, fontWeight: 900,
                color: DA.greenDeep, letterSpacing: '-0.03em', lineHeight: 1, marginTop: 4,
                whiteSpace: 'nowrap',
              }}>
                {isQuote ? 'Sur devis' : `${fmt(prefill.total_ht)} €`}
              </p>
              <p style={{
                fontFamily: DA.mono, fontSize: 9, color: DA.goldDeep, marginTop: 2,
                letterSpacing: '0.16em', fontWeight: 700,
              }}>
                HT
              </p>
            </div>
          </div>
        </article>

        {/* ─── MODE CHOICE ─── */}
        {!mode ? (
          <div className="mt-6 flex flex-col gap-3" data-testid="mode-choice">
            {/* Option A — Payer maintenant (via contract) */}
            {!isQuote && (
              <button
                onClick={() => setMode('pay')}
                className="text-left transition-all"
                style={{
                  background: DA.greenDeep,
                  color: DA.ivory,
                  borderRadius: 18,
                  padding: '20px 22px',
                  border: `0.5px solid ${DA.goldMain}`,
                  boxShadow: '0 20px 40px -18px rgba(11,43,36,0.35)',
                  cursor: 'pointer',
                }}
                data-testid="mode-pay"
              >
                <div className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center flex-shrink-0" style={{
                    width: 42, height: 42, borderRadius: 12,
                    background: 'rgba(216,148,64,0.15)', border: `0.5px solid ${DA.goldMain}`,
                  }}>
                    <CreditCard size={20} strokeWidth={1.8} style={{ color: DA.goldMain }} />
                  </span>
                  <div className="flex-1">
                    <p style={{
                      fontFamily: DA.mono, fontSize: 9.5, letterSpacing: '0.22em',
                      textTransform: 'uppercase', color: DA.goldMain, fontWeight: 800,
                    }}>
                      Option 1 · La plus rapide
                    </p>
                    <h3 style={{
                      fontFamily: DA.serif, fontSize: 20, fontWeight: 900,
                      color: DA.ivory, letterSpacing: '-0.022em', lineHeight: 1.1, marginTop: 4,
                    }}>
                      Réserver & payer maintenant
                    </h3>
                    <p style={{
                      fontFamily: DA.sans, fontSize: 13, color: 'rgba(247,238,223,0.75)',
                      lineHeight: 1.5, marginTop: 6,
                    }}>
                      Vous laissez vos coordonnées, signez votre contrat en ligne, puis planifiez votre créneau.
                    </p>
                  </div>
                </div>
              </button>
            )}

            {/* Option B — WhatsApp appel Sabril */}
            <a
              href={buildWhatsappUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-left transition-all"
              style={{
                background: DA.paper,
                color: DA.greenDeep,
                borderRadius: 18,
                padding: '20px 22px',
                border: `0.5px solid ${DA.goldMain}`,
                boxShadow: '0 14px 30px -14px rgba(11,43,36,0.16)',
              }}
              data-testid="mode-whatsapp"
            >
              <div className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center flex-shrink-0" style={{
                  width: 42, height: 42, borderRadius: 12,
                  background: DA.goldSofter, border: `0.5px solid ${DA.goldSoft}`,
                }}>
                  <MessageCircle size={20} strokeWidth={1.8} style={{ color: DA.goldDeep }} />
                </span>
                <div className="flex-1">
                  <p style={{
                    fontFamily: DA.mono, fontSize: 9.5, letterSpacing: '0.22em',
                    textTransform: 'uppercase', color: DA.goldDeep, fontWeight: 800,
                  }}>
                    Option 2 · Discuter d&apos;abord
                  </p>
                  <h3 style={{
                    fontFamily: DA.serif, fontSize: 20, fontWeight: 900,
                    color: DA.greenDeep, letterSpacing: '-0.022em', lineHeight: 1.1, marginTop: 4,
                  }}>
                    Un appel avec Sabril
                  </h3>
                  <p style={{
                    fontFamily: DA.sans, fontSize: 13, color: DA.inkMuted,
                    lineHeight: 1.5, marginTop: 6,
                  }}>
                    RDV de 30 min sur WhatsApp — je valide avec vous les chantiers prioritaires avant tout engagement.
                  </p>
                </div>
              </div>
            </a>

            {/* Trust chip */}
            <div className="mt-2 flex items-center justify-center gap-2" data-testid="trust-chip">
              <ShieldCheck size={13} strokeWidth={2.2} style={{ color: DA.goldDeep }} />
              <p style={{
                fontFamily: DA.mono, fontSize: 10, letterSpacing: '0.20em',
                textTransform: 'uppercase', color: DA.goldDeep, fontWeight: 700,
              }}>
                Signature électronique · Valeur légale
              </p>
            </div>
          </div>
        ) : (
          /* ─── PAY FLOW · Contact form ─── */
          <div className="mt-6" data-testid="pay-flow">
            <button
              onClick={() => setMode(null)}
              className="inline-flex items-center gap-1.5 mb-5"
              style={{
                fontFamily: DA.mono, fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase',
                color: DA.inkMuted, fontWeight: 700,
              }}
              data-testid="pay-back"
            >
              <ChevronLeft size={12} strokeWidth={2.4} />
              Choisir une autre option
            </button>

            <p style={{
              fontFamily: DA.mono, fontSize: 10, letterSpacing: '0.28em',
              textTransform: 'uppercase', color: DA.goldDeep, fontWeight: 800, marginBottom: 14,
            }}>
              Vos coordonnées
            </p>

            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Prénom" value={contact.firstName} onChange={(v) => setContact({ ...contact, firstName: v })} testid="input-firstname" />
              <Field label="Restaurant" value={contact.restaurant} onChange={(v) => setContact({ ...contact, restaurant: v })} testid="input-restaurant" />
              <Field label="Email" type="email" value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} testid="input-email" />
              <Field label="WhatsApp / Téléphone" value={contact.phone} onChange={(v) => setContact({ ...contact, phone: v })} testid="input-phone" placeholder="+33 6 12 34 56 78" />
              <Field label="Notes (optionnel)" value={contact.notes} onChange={(v) => setContact({ ...contact, notes: v })} testid="input-notes" multiline className="sm:col-span-2" />
            </div>

            <button
              onClick={handlePaySubmit}
              disabled={!canSubmit || submitting}
              className="mt-6 w-full flex items-center justify-center gap-2 transition-all"
              style={{
                background: !canSubmit || submitting ? DA.inkFaint : DA.greenDeep,
                color: DA.ivory,
                padding: '16px 24px',
                borderRadius: 14,
                fontFamily: DA.mono, fontSize: 12, letterSpacing: '0.22em',
                textTransform: 'uppercase', fontWeight: 800,
                opacity: !canSubmit || submitting ? 0.6 : 1,
                cursor: !canSubmit || submitting ? 'not-allowed' : 'pointer',
              }}
              data-testid="submit-pay"
            >
              <FileSignature size={16} strokeWidth={2} />
              {submitting ? 'Envoi…' : 'Continuer vers la signature'}
            </button>

            <p style={{
              fontFamily: DA.sans, fontSize: 11, color: DA.inkFaint, textAlign: 'center',
              marginTop: 10, lineHeight: 1.5,
            }}>
              Vous serez ensuite dirigé vers la page de signature du contrat.
              Après signature, je vous contacte pour planifier votre créneau.
            </p>
          </div>
        )}

        <div className="mt-10 text-center">
          <SabrilDivider tagline="Sabril · Audit Resto" size={16} />
          <div className="mt-3 flex justify-center opacity-70">
            <SabrilSignature size={40} color={DA.greenDeep} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', testid, placeholder, multiline, className = '' }) {
  const Tag = multiline ? 'textarea' : 'input';
  return (
    <label className={`block ${className}`}>
      <span style={{
        fontFamily: DA.mono, fontSize: 9.5, letterSpacing: '0.22em',
        textTransform: 'uppercase', color: DA.inkMuted, fontWeight: 700,
        display: 'block', marginBottom: 6,
      }}>
        {label}
      </span>
      <Tag
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={multiline ? 3 : undefined}
        data-testid={testid}
        className="w-full transition-all"
        style={{
          background: DA.paper,
          border: `0.5px solid ${DA.goldSoft}`,
          borderRadius: 10,
          padding: '11px 14px',
          fontFamily: DA.sans, fontSize: 14, color: DA.greenDeep,
          outline: 'none',
          resize: multiline ? 'vertical' : 'none',
        }}
        onFocus={(e) => { e.target.style.borderColor = DA.goldMain; }}
        onBlur={(e) => { e.target.style.borderColor = DA.goldSoft; }}
      />
    </label>
  );
}
